/**
 * tenant-tab-meta — słownik etykiet + ikon kart dla powłoki TENANTA.
 *
 * Wylicza `{ label, icon }` ze ścieżki (dla `TabProvider`). Dla znanych sekcji bierze
 * etykietę z i18n + ikonę lucide; dla ścieżek szczegółów/podstron — etykietę „pojedynczą"
 * (np. „Kontrahent") albo z sufiksem. Realna nazwa rekordu (np. konkretny kontrahent)
 * może być później podstawiona przez stronę szczegółów wołającą `updateTabLabel`.
 */

import type { TabMeta } from '@/contexts/TabContext';
import i18n from '@/i18n';

export const TENANT_TABS_STORAGE_KEY = 'saas_tenant_open_tabs';
export const TENANT_DEFAULT_PATH = '/dashboard';

interface SectionMeta {
  /** klucz i18n etykiety listy/sekcji (tenant.tabs.titles.<key>) */
  key: string;
  icon: string;
  /** klucz i18n etykiety pojedynczego rekordu (tenant.tabs.detail.<detailKey>) */
  detailKey?: string;
}

/** Mapa: pierwszy segment ścieżki → metadane sekcji. */
const SECTIONS: Record<string, SectionMeta> = {
  dashboard: { key: 'dashboard', icon: 'LayoutDashboard' },
  companies: { key: 'companies', icon: 'Building2', detailKey: 'company' },
  contractors: { key: 'contractors', icon: 'Contact', detailKey: 'contractor' },
  'contractor-contacts': {
    key: 'contractorContacts',
    icon: 'Users',
    detailKey: 'contractorContact',
  },
  products: { key: 'products', icon: 'Package', detailKey: 'product' },
  'purchase-invoices': {
    key: 'purchaseInvoices',
    icon: 'ReceiptText',
    detailKey: 'purchaseInvoice',
  },
  'sale-invoices': {
    key: 'saleInvoices',
    icon: 'ReceiptText',
    detailKey: 'saleInvoice',
  },
  ksef: { key: 'ksef', icon: 'FileCheck' },
  contracts: { key: 'contracts', icon: 'File', detailKey: 'contract' },
  letters: { key: 'letters', icon: 'Mail', detailKey: 'letter' },
  'commercial-docs': { key: 'commercialDocs', icon: 'Briefcase', detailKey: 'commercialDoc' },
  'internal-docs': { key: 'internalDocs', icon: 'Shield', detailKey: 'internalDoc' },
  'special-docs': { key: 'specialDocs', icon: 'Star', detailKey: 'specialDoc' },
  employees: { key: 'employees', icon: 'IdCard', detailKey: 'employee' },
  'org-chart': { key: 'orgChart', icon: 'Building2' },
  hr: { key: 'hr', icon: 'Users' },
  calendar: { key: 'calendar', icon: 'Calendar' },
  'area-map': { key: 'areaMap', icon: 'Map' },
  grafik: { key: 'grafik', icon: 'Calendar' },
  users: { key: 'users', icon: 'Users', detailKey: 'user' },
  settings: { key: 'settings', icon: 'Settings' },
  profile: { key: 'profile', icon: 'User' },
  modules: { key: 'modules', icon: 'Boxes' },
  'system-modules': { key: 'systemModules', icon: 'Boxes' },
  processes: { key: 'processes', icon: 'GitBranch' },
  'menu-builder': { key: 'menuBuilder', icon: 'SquareMenu' },
  tenants: { key: 'tenants', icon: 'Building2' },
};

const DEFAULT_ICON = 'File';
const DASHBOARD_ICON = 'LayoutDashboard';

/**
 * Sekcje ekranu Ustawień jako OSOBNE karty (`/settings/<section>`): pełny klucz i18n etykiety
 * + nazwa ikony (z `ICON_MAP` w `components/modules/IconPicker`). Utrzymuj zgodnie z grupami
 * w `pages/Settings.tsx`. Pozycje-linki (menu-builder, process-definitions, default-dashboard)
 * NIE są tu — nawigują wprost do własnych tras (`/menu-builder`, `/processes`, `/dashboard/default`),
 * które mają własne wpisy w SECTIONS. Profil = strona-lądowanie `/settings` (karta „Ustawienia"),
 * więc też nie musi tu być.
 */
export const SETTINGS_SECTIONS: Record<string, { key: string; icon: string }> = {
  permissions: { key: 'tenant.settings.tabs.permissions', icon: 'Shield' },
  'org-structure': { key: 'tenant.settings.tabs.orgStructure', icon: 'Building2' },
  dictionaries: { key: 'tenant.settings.tabs.dictionaries', icon: 'Book' },
  'non-working-days': { key: 'tenant.settings.tabs.nonWorkingDays', icon: 'Calendar' },
  'record-link-types': { key: 'tenant.settings.tabs.recordLinkTypes', icon: 'Link' },
  widgets: { key: 'tenant.settings.tabs.widgets', icon: 'BarChart3' },
  'document-types': { key: 'tenant.settings.tabs.documentTypes', icon: 'FileText' },
  'record-categories': { key: 'tenant.settings.tabs.recordCategories', icon: 'Tag' },
  'sql-procedures': { key: 'tenant.settings.tabs.sqlProcedures', icon: 'List' },
  'chart-of-accounts': { key: 'tenant.settings.tabs.chartOfAccounts', icon: 'Wallet' },
  'decree-templates': { key: 'tenant.settings.tabs.decreeTemplates', icon: 'FileText' },
  'decree-procedures': { key: 'decreeProcedures.tabLabel', icon: 'FileText' },
  modules: { key: 'tenant.settings.tabs.modules', icon: 'Package' },
  integrations: { key: 'tenant.settings.tabs.integrations', icon: 'Server' },
  ksef: { key: 'tenant.settings.tabs.ksef', icon: 'Receipt' },
  'modules-overview': { key: 'tenant.settings.tabs.modulesOverview', icon: 'Layers' },
  'module-fields': { key: 'tenant.settings.tabs.moduleFields', icon: 'List' },
  'additional-tables': { key: 'additionalTables.admin.tabLabel', icon: 'Database' },
  'additional-views': { key: 'additionalViews.admin.tabLabel', icon: 'Database' },
  billing: { key: 'tenant.settings.tabs.billing', icon: 'CreditCard' },
};

/**
 * Kartoteki modułu „Handel i Magazyn" — etykieta i ikona biorą się z DRUGIEGO segmentu
 * (`/handel-magazyn/<kartoteka>`), bo `SECTIONS` rozpoznaje tylko pierwszy, a wszystkie pięć
 * kartotek dzieli ten sam prefiks. Bez tego każda karta modułu nazywałaby się „Handel-magazyn".
 */
const HANDEL_MAGAZYN_SECTIONS: Record<string, { key: string; icon: string; detailKey?: string }> = {
  sprzedaz: { key: 'tenant.handelMagazyn.sales.title', icon: 'Receipt', detailKey: 'document' },
  magazyn: { key: 'tenant.handelMagazyn.warehouse.title', icon: 'Box', detailKey: 'document' },
  zamowienia: {
    key: 'tenant.handelMagazyn.orders.title',
    icon: 'Clipboard',
    detailKey: 'document',
  },
  'kasa-bank': {
    key: 'tenant.handelMagazyn.payments.title',
    icon: 'Wallet',
    detailKey: 'document',
  },
  towary: {
    key: 'tenant.handelMagazyn.products.title',
    icon: 'Package',
    // Karta towaru to osobna zakładka; numer podmienia ją na kod towaru po wczytaniu.
    detailKey: 'product',
  },
  kontrahenci: {
    key: 'tenant.handelMagazyn.contractors.title',
    icon: 'Users',
    // Karta kontrahenta to osobna zakładka obok kartoteki — jak przy towarach.
    detailKey: 'contractor',
  },
  polaczenie: { key: 'tenant.handelMagazyn.connection.title', icon: 'Server' },
};

/** Zestawienia kartotek — nazwa raportu (ze ŚCIEŻKI) jest etykietą jego karty. */
const HANDEL_MAGAZYN_REPORTS: Record<string, string> = {
  'obroty-za-okres': 'tenant.handelMagazyn.productReports.turnover.title',
  'stany-na-dzien': 'tenant.handelMagazyn.productReports.stockOnDate.title',
  'stany-i-obroty': 'tenant.handelMagazyn.productReports.stockAndTurnover.title',
  'stany-i-rezerwacje': 'tenant.handelMagazyn.productReports.stockAndReservations.title',
  'ponizej-minimum': 'tenant.handelMagazyn.productReports.belowMin.title',
  'powyzej-maksimum': 'tenant.handelMagazyn.productReports.aboveMax.title',
  zalegajace: 'tenant.handelMagazyn.productReports.slowMoving.title',
  rotacja: 'tenant.handelMagazyn.productReports.rotation.title',
  'magazyn-za-okres': 'tenant.handelMagazyn.warehouseReports.byPeriod.title',
  'wg-towarow': 'tenant.handelMagazyn.warehouseReports.byProduct.title',
  nierozliczone: 'tenant.handelMagazyn.warehouseReports.unsettled.title',
  'rejestry-dokumentow': 'tenant.handelMagazyn.warehouseReports.registers.title',
  'sprzedaz-za-okres': 'tenant.handelMagazyn.reports.salesByPeriod.tab',
  'sprzedaz-wg-kontrahentow': 'tenant.handelMagazyn.reports.salesByContractor.title',
  'sprzedaz-wg-towarow': 'tenant.handelMagazyn.reports.salesByProduct.title',
  'rejestr-vat-sprzedazy': 'tenant.handelMagazyn.reports.vatRegister.title',
};

function tt(key: string): string {
  return i18n.t(`tenant.tabs.titles.${key}`, { defaultValue: key });
}

/** Czy segment wygląda na identyfikator (UUID / numeryczny). */
function looksLikeId(seg: string): boolean {
  return /^[0-9a-f-]{8,}$/i.test(seg) || /^\d+$/.test(seg);
}

export function resolveTenantTabMeta(path: string): TabMeta {
  const clean = (path.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  const segments = clean.split('/').filter(Boolean);

  const first = segments[0];
  if (!first) {
    return { label: tt('dashboard'), icon: DASHBOARD_ICON };
  }

  // Ustawienia: każda sekcja to osobna karta (/settings/<section>) z własną etykietą + ikoną.
  // Samo /settings (bez drugiego segmentu) spada niżej do SECTIONS['settings'] → „Ustawienia".
  if (first === 'settings' && segments[1]) {
    const s = SETTINGS_SECTIONS[segments[1]];
    if (s) {
      return { label: i18n.t(s.key, { defaultValue: segments[1] }), icon: s.icon };
    }
  }

  // Handel i Magazyn: kartoteka rozpoznawana po drugim segmencie; szczegół dokumentu dostaje
  // etykietę ogólną, a formatka podmienia ją na numer dokumentu po wczytaniu danych.
  if (first === 'handel-magazyn' && segments[1]) {
    const hm = HANDEL_MAGAZYN_SECTIONS[segments[1]];
    if (hm) {
      // Formularz nowego dokumentu dostaje własną etykietę — „Sprzedaż" sugerowałaby listę,
      // a to osobna karta, którą trzeba odróżnić od kartoteki obok.
      if (segments[2] === 'nowy') {
        // Każda kartoteka ma własny formularz — etykieta musi mówić, CZEGO dotyczy, bo karty
        // stoją obok siebie w pasku.
        const newKey =
          segments[1] === 'kasa-bank'
            ? 'tenant.handelMagazyn.newPayment.tab'
            : segments[1] === 'magazyn'
              ? 'tenant.handelMagazyn.newWarehouse.tab'
              : segments[1] === 'towary'
                ? 'tenant.handelMagazyn.newProduct.tab'
                : segments[1] === 'kontrahenci'
                  ? 'tenant.handelMagazyn.contractors.newTab'
                  : 'tenant.handelMagazyn.newSale.tab';
        return { label: i18n.t(newKey), icon: hm.icon };
      }
      // Zestawienie to osobna karta od kartoteki — ma stać OBOK niej, a nie ją podmieniać.
      // Etykieta bierze się z nazwy raportu, bo raportów będzie więcej niż jeden.
      if (segments[2] === 'zestawienia' && segments[3]) {
        const reportKey = HANDEL_MAGAZYN_REPORTS[segments[3]];
        if (reportKey) return { label: i18n.t(reportKey), icon: 'BarChart3' };
      }
      // Edycja to osobna karta od podglądu — inaczej obie trafiłyby w jedną i formularz
      // podmieniłby otwarty podgląd.
      if (segments[3] === 'edycja') {
        // Etykieta MUSI mówić, czego dotyczy edycja — kartoteki stoją obok siebie w pasku.
        const editKey =
          segments[1] === 'kontrahenci'
            ? 'tenant.handelMagazyn.contractors.editTab'
            : 'tenant.handelMagazyn.newWarehouse.editTab';
        return { label: i18n.t(editKey), icon: hm.icon };
      }
      const isDetail = segments.slice(2).some(looksLikeId);
      if (isDetail && hm.detailKey) {
        return {
          label: i18n.t(`tenant.tabs.detail.${hm.detailKey}`, {
            defaultValue: i18n.t(hm.key, { defaultValue: segments[1] }),
          }),
          icon: hm.icon,
        };
      }
      return { label: i18n.t(hm.key, { defaultValue: segments[1] }), icon: hm.icon };
    }
  }

  // Kartoteka modułu (/modules/<slug>/…): ze ścieżki nie wynika, czy jest systemowa
  // (zainstalowana przez ITE), czy własna tenanta — etykieta neutralna, a strona po
  // wczytaniu podmienia ją na nazwę kartoteki (`useTabLabel`); po odświeżeniu strony
  // zostaje zapisana nazwa (`keepStoredLabel`), nie zastępstwo.
  if (first === 'modules' && segments[1]) {
    return {
      label: tt('kartoteka'),
      icon: SECTIONS.modules?.icon ?? DEFAULT_ICON,
      keepStoredLabel: true,
    };
  }

  const section = SECTIONS[first];
  if (!section) {
    // Nieznana sekcja — kapitalizacja pierwszego segmentu.
    return { label: first.charAt(0).toUpperCase() + first.slice(1), icon: DEFAULT_ICON };
  }

  const last = segments[segments.length - 1];

  // Tworzenie nowego rekordu LUB szczegóły rekordu (segment-id) → etykieta „pojedyncza"
  // (np. „Kontrahent") jeśli sekcja ją ma; inaczej etykieta sekcji.
  const isDetailLike = last === 'new' || segments.some(looksLikeId);
  if (isDetailLike && section.detailKey) {
    return {
      label: i18n.t(`tenant.tabs.detail.${section.detailKey}`, { defaultValue: tt(section.key) }),
      icon: section.icon,
    };
  }

  return { label: tt(section.key), icon: section.icon };
}
